import { createContext, useContext, useEffect, useState, ReactNode, useCallback } from "react";
import { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";


interface UserProfile {
  id: string;
  email: string | null;
  display_name: string | null;
  avatar_url: string | null;
  plan_id: string | null;
  is_banned: boolean;
  searches_today: number;
}

interface AuthContextType {
  session: Session | null;
  user: User | null;
  profile: UserProfile | null;
  roles: string[];
  isAdmin: boolean;
  isLoading: boolean;
  signUp: (email: string, password: string, displayName?: string) => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

async function ensureProfile(userId: string, userEmail: string | null | undefined): Promise<UserProfile | null> {
  // Try to fetch existing profile first
  const { data, error } = await supabase
    .from("profiles")
    .select("*")
    .eq("id", userId)
    .single();

  if (data) return data as UserProfile;

  // Profile missing — auto-create with safe fallback
  if (error?.code === "PGRST116") {
    const email = userEmail ?? null;
    const displayName = email ? email.split("@")[0] : "User";

    await supabase.from("profiles").upsert(
      { id: userId, email, display_name: displayName },
      { onConflict: "id" }
    );

    // Role assignment handled by server-side trigger (handle_new_user)

    const { data: retried } = await supabase
      .from("profiles")
      .select("*")
      .eq("id", userId)
      .single();
    return retried as UserProfile | null;
  }

  return null;
}

async function fetchRoles(userId: string): Promise<string[]> {
  // Retry up to 3 times — roles can lag slightly on first insert from trigger
  for (let attempt = 0; attempt < 3; attempt++) {
    const { data, error } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", userId);
    if (!error && data && data.length > 0) {
      return data.map((r: { role: string }) => r.role);
    }
    if (attempt < 2) await new Promise((r) => setTimeout(r, 400 * (attempt + 1)));
  }
  return [];
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [roles, setRoles] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const loadUserData = useCallback(async (sessionUser: User, accessToken?: string) => {
    const [profileResult, rolesResult] = await Promise.all([
      ensureProfile(sessionUser.id, sessionUser.email),
      fetchRoles(sessionUser.id),
    ]);
    setProfile(profileResult);
    setRoles(rolesResult);
  }, []);

  const refreshProfile = useCallback(async () => {
    if (!user) return;
    const { data: { session } } = await supabase.auth.getSession();
    await loadUserData(user, session?.access_token);
  }, [user, loadUserData]);

  useEffect(() => {
    // Set up auth state listener BEFORE calling getSession
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (_event, newSession) => {
        setSession(newSession);
        setUser(newSession?.user ?? null);

        if (newSession?.user) {
          // Use setTimeout to avoid Supabase deadlock during onAuthStateChange
          setTimeout(() => {
            loadUserData(newSession.user, newSession.access_token);
          }, 0);
        } else {
          setProfile(null);
          setRoles([]);
        }
        setIsLoading(false);
      }
    );

    // Hydrate existing session
    supabase.auth.getSession().then(({ data: { session: existingSession } }) => {
      setSession(existingSession);
      setUser(existingSession?.user ?? null);
      if (existingSession?.user) {
        loadUserData(existingSession.user, existingSession.access_token);
      }
      setIsLoading(false);
    });

    return () => subscription.unsubscribe();
  }, [loadUserData]);

  const signUp = async (email: string, password: string, displayName?: string) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/auth`,
        data: { display_name: displayName || email.split("@")[0] },
      },
    });
    if (error) throw error;
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setProfile(null);
    setRoles([]);
    setSession(null);
    setUser(null);
  };

  const isAdmin = roles.includes("admin") || roles.includes("super_admin");

  return (
    <AuthContext.Provider
      value={{ session, user, profile, roles, isAdmin, isLoading, signUp, signIn, signOut, refreshProfile }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
}
